import React, { useState, useEffect } from 'react';
import { MapPin, AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import DatePickerWithValidation from './DatePickerWithValidation';
import ConflictWarnings from './ConflictWarnings';
import { useJobs } from '../../hooks/useJobs';
import { generateWorkdays } from '../../utils/work-day-calculator';
import { isoDateString, dateFromInput, dateToInput } from '../../utils/date-utils';

export default function MoveJobDialog({ 
  isOpen, 
  onClose, 
  job
}) {
  const { jobs, updateJob } = useJobs();
  const [moveMode, setMoveMode] = useState('entire'); // 'entire' or 'single'
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [newDate, setNewDate] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && job) {
      setMoveMode('entire');
      setSelectedDayIndex(0);
      setNewDate(job.scheduledDate ? dateToInput(job.scheduledDate) : '');
      setConflicts([]);
      setError('');
    }
  }, [isOpen, job]);

  // Check for conflicts when date changes
  useEffect(() => {
    if (newDate && job) {
      checkConflicts();
    }
  }, [newDate, job, moveMode, selectedDayIndex]);

  function checkConflicts() {
    if (!newDate || !job) return;

    let workDayStrings = [];

    if (moveMode === 'entire') {
      // Check conflicts for entire job
      const duration = job.durationDays || 1;
      const workDays = generateWorkdays(dateFromInput(newDate), duration);
      workDayStrings = workDays.map(d => isoDateString(d));
    } else {
      // Check conflicts for single day
      workDayStrings = [isoDateString(dateFromInput(newDate))];
    }

    const overlapping = jobs.filter(j => {
      if (!j.scheduledDate || j.id === job.id) return false;
      
      // Check if crew matches
      if (job.assignedCrew && j.assignedCrew !== job.assignedCrew) return false;

      const jobDates = generateWorkdays(new Date(j.scheduledDate), j.durationDays || 1);
      const jobDateStrings = jobDates.map(d => isoDateString(d));

      return workDayStrings.some(date => jobDateStrings.includes(date));
    });

    setConflicts(overlapping);
  }

  async function handleMove() {
    if (!newDate) {
      setError('Please select a new date');
      return;
    }

    setSaving(true);

    try {
      if (moveMode === 'entire') {
        // Move entire job - regenerate all work days
        const workDays = generateWorkdays(dateFromInput(newDate), job.durationDays || 1).map(date => ({
          id: isoDateString(date),
          date: date,
          status: 'planned',
          note: null,
          createdAt: new Date()
        }));

        await updateJob(job.id, {
          scheduledDate: dateFromInput(newDate),
          workDays: workDays,
          statusHistory: [
            ...(job.statusHistory || []),
            {
              status: job.status,
              timestamp: new Date(),
              note: `Entire job rescheduled to ${dateFromInput(newDate).toLocaleDateString()}`
            }
          ]
        });

        console.log('✅ Entire job moved successfully');
      } else {
        // Move single day only
        const currentWorkDays = job.workDays || [];
        const newWorkDays = [...currentWorkDays];

        // Update the selected day
        newWorkDays[selectedDayIndex] = {
          ...newWorkDays[selectedDayIndex],
          date: dateFromInput(newDate),
          id: isoDateString(dateFromInput(newDate)),
          note: `Moved from ${new Date(currentWorkDays[selectedDayIndex].date).toLocaleDateString()}`
        };

        // Sort by date
        newWorkDays.sort((a, b) => new Date(a.date) - new Date(b.date));

        await updateJob(job.id, {
          workDays: newWorkDays,
          statusHistory: [
            ...(job.statusHistory || []),
            {
              status: job.status,
              timestamp: new Date(),
              note: `Day ${selectedDayIndex + 1} moved to ${dateFromInput(newDate).toLocaleDateString()}`
            }
          ]
        });

        console.log('✅ Single day moved successfully');
      }

      onClose();
    } catch (error) {
      console.error('❌ Error moving job:', error);
      setError('Failed to move job. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (!job) return null;

  const workDays = job.workDays || [];
  const hasMultipleDays = workDays.length > 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Move Job"
      size="medium"
    >
      <div style={{ padding: '24px' }}>
        {/* Job Info */}
        <div style={{
          padding: '16px',
          background: 'var(--brand-brown-tint-soft)',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '4px'
          }}>
            #{job.contractNumber} - {job.customerName}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: 'var(--text-secondary)',
            marginBottom: '8px'
          }}>
            <MapPin size={13} strokeWidth={2.25} /> {job.name}
          </div>
          {hasMultipleDays && (
            <div style={{
              fontSize: '12px',
              color: 'var(--brand-brown)',
              fontWeight: '600'
            }}>
              {workDays.length}-day installation
            </div>
          )}
        </div>

        {/* Move Mode Selection (only show if multi-day job) */}
        {hasMultipleDays && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '10px'
            }}>
              What do you want to move?
            </label>

            {/* Move Entire Job */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              border: moveMode === 'entire' ? '2px solid var(--brand-brown)' : '1px solid var(--input-border)',
              borderRadius: '8px',
              marginBottom: '8px',
              cursor: 'pointer',
              background: moveMode === 'entire' ? 'var(--brand-brown-tint-soft)' : 'var(--bg-card)'
            }}>
              <input
                type="radio"
                name="moveMode"
                value="entire"
                checked={moveMode === 'entire'}
                onChange={(e) => setMoveMode(e.target.value)}
                style={{ marginRight: '10px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Move entire job
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  All {workDays.length} days will move together to a new start date
                </div>
              </div>
            </label>

            {/* Move Single Day */}
            <label style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              border: moveMode === 'single' ? '2px solid var(--brand-brown)' : '1px solid var(--input-border)',
              borderRadius: '8px',
              cursor: 'pointer',
              background: moveMode === 'single' ? 'rgba(139, 69, 19, 0.05)' : 'var(--bg-card)'
            }}>
              <input
                type="radio"
                name="moveMode"
                value="single"
                checked={moveMode === 'single'}
                onChange={(e) => setMoveMode(e.target.value)}
                style={{ marginRight: '10px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Move single day only
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Move one specific day, other days stay in place
                </div>
              </div>
            </label>
          </div>
        )}

        {/* Day Selector (only show if moving single day) */}
        {moveMode === 'single' && hasMultipleDays && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '6px'
            }}>
              Which day to move?
            </label>
            <select
              value={selectedDayIndex}
              onChange={(e) => setSelectedDayIndex(parseInt(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--input-border)',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                background: 'var(--bg-tertiary)'
              }}
            >
              {workDays.map((day, index) => (
                <option key={index} value={index}>
                  Day {index + 1} - {new Date(day.date).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Conflict Warnings */}
        <ConflictWarnings conflicts={conflicts} />

        {/* New Date Picker */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '6px'
          }}>
            {moveMode === 'entire' ? 'New start date' : 'New date for selected day'}
          </label>
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: error && !newDate ? '1px solid var(--status-text-bold-red)' : '1px solid var(--input-border)',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            background: 'rgba(220, 38, 38, 0.05)',
            border: '1px solid rgba(220, 38, 38, 0.2)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: 'var(--status-text-bold-red)',
            marginBottom: '20px'
          }}>
            <AlertTriangle size={13} strokeWidth={2.25} /> {error}
          </div>
        )}

        {/* Info Message */}
        <div style={{
          padding: '12px',
          background: 'rgba(59, 130, 246, 0.05)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '8px',
          fontSize: '13px',
          color: 'var(--status-text-bold-blue)'
        }}>
          {moveMode === 'entire' ? (
            <>ℹ️ Moving entire job will update all {workDays.length || 1} work day{(workDays.length || 1) !== 1 ? 's' : ''} to start from the new date. Weekends will be skipped automatically.</>
          ) : (
            <>ℹ️ Only the selected day will be moved. Other days will remain on their original dates. You can track all days on the calendar.</>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid var(--cell-border)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        background: 'var(--bg-secondary)'
      }}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleMove}
          disabled={saving || !newDate}
        >
          {saving ? 'Moving...' : moveMode === 'entire' ? 'Move Entire Job' : 'Move Day'}
        </Button>
      </div>
    </Modal>
  );
}